import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Footer as FableFooter } from "@/ds";
import { useSmartCTA } from "@/hooks/useSmartCTA";

/**
 * The site footer, on Fable (DY1.4).
 *
 * The surface is `--charcoal-lift`, not the retired `--espresso` brown: a warm
 * brown next to the logo's cool black read as a mistake, so the dark surface is
 * now derived from the logo's own black (BRIEF-FABLE.md §2).
 *
 * Destinations still come from `useSmartCTA`, so a signed-in FixyBee clicking
 * "Become a FixyBee" lands on their dashboard rather than the acquisition page.
 */
export default function Footer() {
  const cta = useSmartCTA();
  const { t } = useTranslation();

  const columns = [
    {
      title: t("footer.forClients"),
      links: [
        { label: t("footer.findFixybees"), href: "/providers" },
        { label: t("footer.postJob"), href: cta.postJob() },
        { label: t("footer.howItWorks"), href: "/how-it-works" },
      ],
    },
    {
      title: t("footer.forFixybees"),
      links: [
        // Sitemap link, not a CTA — but `becomeFixyBee()` already points a
        // FixyBee at their own dashboard, so the label was the last place still
        // inviting them to join.
        {
          label: cta.isProvider ? t("footer.myDashboard") : t("footer.becomeFixybee"),
          href: cta.becomeFixyBee(),
        },
        { label: t("footer.plansPricing"), href: "/pricing" },
      ],
    },
    {
      title: t("footer.resources"),
      links: [
        { label: t("footer.terms"), href: "/terms" },
        { label: t("footer.privacy"), href: "/privacy" },
        { label: t("footer.refund"), href: "/refund-policy" },
        { label: t("footer.faqLink"), href: "/faq" },
        { label: t("footer.guides"), href: "/guides" },
      ],
    },
  ];

  return (
    <div data-testid="footer">
      <FableFooter
        blurb={t("faq.items.q1.a")}
        columns={columns}
        legal={t("footer.rights")}
        builtBy={t("footer.builtBy")}
        renderLink={({ href, label, style }) => (
          <Link to={href} style={style}>{label}</Link>
        )}
      />
    </div>
  );
}
